import { Task } from "@/data/models/Task";
import { DateTime } from "luxon";
import { getDateFromYearMonthDateString } from "../DateHelper";

enum Mode {
  DEFAULT = "DEFAULT",
  FIXED = "FIXED",
  START_AT = "START_AT",
}

export class DaysIterator {
  private mode: Mode;

  private task: Task;
  private startDate: Date;
  private currentDateTime: DateTime | null;
  private skipWeekends: boolean;

  constructor(
    startDate: Date,
    task: Task,
    skipWeekends: boolean,
    isStartDateForced?: boolean
  ) {
    this.startDate = startDate;
    this.task = task;

    const { targetDate, isTargetDateFixed } = task.planningParameters;

    if (targetDate) {
      this.mode = isTargetDateFixed ? Mode.FIXED : Mode.START_AT;
    } else {
      this.mode = Mode.DEFAULT;
    }

    this.skipWeekends = skipWeekends;

    this.currentDateTime = isStartDateForced
      ? DateTime.fromJSDate(startDate)
      : this.calculateInitialDateTime(this.mode);
  }

  private shouldDateTimeBeSkipped(dateTime: DateTime) {
    return this.skipWeekends && dateTime.weekday >= 6;
  }

  private calculateInitialDateTime(mode: Mode): DateTime | null {
    switch (mode) {
      case Mode.DEFAULT: {
        const dateTime = DateTime.fromJSDate(this.startDate)
          .plus({ hour: 1 })
          .set({
            minute: 0,
            second: 0,
            millisecond: 0,
          });

        return this.shouldDateTimeBeSkipped(dateTime)
          ? this.advanceDateTime(dateTime)
          : dateTime;
      }

      case Mode.FIXED:
      case Mode.START_AT: {
        const targetDate =
          getDateFromYearMonthDateString(
            this.task.planningParameters.targetDate
          ) ?? this.startDate;

        const dateTime = DateTime.fromJSDate(targetDate);

        const startOfToday = DateTime.fromJSDate(new Date())
          .startOf("day")
          .valueOf();

        if (dateTime.valueOf() < startOfToday) {
          //Fixed date is before now, so we won't be able to find a fitting time slot

          if (mode == Mode.FIXED) {
            return null;
          } else {
            this.mode = Mode.DEFAULT;
            return this.calculateInitialDateTime(Mode.DEFAULT);
          }
        }

        return dateTime;
      }
    }
  }

  private advanceDateTime(dateTime: DateTime): DateTime {
    let next = dateTime;

    do {
      next = next.plus({ day: 1 });
    } while (this.shouldDateTimeBeSkipped(next));

    return next;
  }

  getNextDateTime(): DateTime | null {
    const dateTime = this.currentDateTime;

    if (!dateTime) {
      return null;
    }

    switch (this.mode) {
      case Mode.FIXED: {
        this.currentDateTime = null;
        break;
      }
      case Mode.START_AT: {
        this.mode = Mode.DEFAULT;
        this.currentDateTime = this.calculateInitialDateTime(this.mode);
        break;
      }
      case Mode.DEFAULT: {
        this.currentDateTime = this.advanceDateTime(dateTime);
        break;
      }
    }

    return dateTime;
  }
}
